// The query behind a model: the SQL that actually builds it.
//
// Three provenances, labelled rather than blended, because "where did this
// SQL come from" changes how much you can trust it:
//
//   dbt      - the model's own .sql file, verbatim, Jinja intact. Not
//              compiled: ref() and source() are left as written so the file
//              on disk and the text here are the same thing.
//   glue     - a CREATE OR REPLACE VIEW rendered by a script under
//              ops/aws/glue/. {silver_db} style placeholders are left in
//              place; substituting a tenant id would misrepresent what runs.
//   external - Bronze landing tables written by the Glue Delta jobs. There
//              is no query to show, and saying so is better than showing an
//              empty box.

import type { LineageGraphNode } from "../types";

const PROVENANCE: Record<string, { label: string; note: string }> = {
  dbt: {
    label: "dbt model",
    note: "Verbatim from the model file. Jinja is left uncompiled.",
  },
  glue: {
    label: "Glue-authored view",
    note: "Rendered by a deploy script. Database placeholders left as-is.",
  },
};

export function QueryPanel({ node }: { node: LineageGraphNode }) {
  const sql = node.sql?.trim() ?? "";

  if (!sql) {
    return (
      <div className="qry-empty">
        <strong>No query builds this table.</strong>
        <p>
          {node.layer === "bronze"
            ? "Bronze landing tables are written by the Glue Delta jobs straight from Raw, so there is no SQL definition to show."
            : "This node was discovered as a dependency but nothing in the repo defines it."}
        </p>
      </div>
    );
  }

  const prov = PROVENANCE[node.sql_kind] ?? {
    label: node.sql_kind,
    note: "",
  };
  const lines = sql.split("\n");

  // Some Glue views are assembled from Python fragments, so the statement
  // carries placeholders like {spine} that stand in for whole CTEs. Saying
  // the text is partial beats letting someone copy it and wonder why it will
  // not run. Database placeholders are expected, so they are excluded.
  const composed = [
    ...new Set(
      (sql.match(/\{[a-zA-Z_][a-zA-Z0-9_]*\}/g) ?? []).filter(
        (m) => !/^\{(bronze|silver|gold)_db\}$/.test(m),
      ),
    ),
  ];

  return (
    <div className="qry">
      <div className="qry-head">
        <span className={`qry-prov qry-prov-${node.sql_kind}`}>{prov.label}</span>
        {prov.note && <span className="qry-note">{prov.note}</span>}
        {node.sql_path && <code className="qry-path">{node.sql_path}</code>}
        <span className="qry-lines">{lines.length} lines</span>
      </div>
      {composed.length > 0 && (
        <p className="qry-partial">
          Assembled in Python. {composed.join(" ")}{" "}
          {composed.length === 1 ? "is a fragment" : "are fragments"} substituted
          at deploy time, so this is the statement's shape rather than the
          finished query.
        </p>
      )}
      <pre className="qry-sql">
        <code>{sql}</code>
      </pre>
    </div>
  );
}
